/* SLEEPOVER v11. Enhance mobile charts without changing measurements or desktop markup. */
(() => {
  'use strict';
  if (window.sleepoverSizeChartsLoaded) return;
  window.sleepoverSizeChartsLoaded = true;
  const mobile = window.matchMedia('(max-width: 989px)');
  const contentSelector = '.rte, .product__description, .product-popup-modal__content-info';
  const candidateSelector = '.rte table, .product__description table, .product-popup-modal__content-info table, .rte img, .product__description img, .product-popup-modal__content-info img';
  const chartPattern = /(?:size|sizing|measurement)[\s_-]*(?:chart|guide|table)|(?:chart|guide)[\s_-]*(?:size|sizing)/i;
  const instances = new Map();
  let sequence = 0;
  let frame = 0;
  const strings = {
    table: 'Size / measurement table',
    image: 'Size chart',
    swipe: 'Swipe left or right to see the full chart.',
    enlarge: 'Enlarge chart',
    fit: 'Fit chart to screen',
    ...(window.sleepoverSizeChartLabels || {})
  };

  function nearbyHeading(node) {
    const scope = node.closest(contentSelector);
    let current = node;
    while (current && current !== scope) {
      let previous = current.previousElementSibling;
      while (previous) {
        if (/^H[1-6]$/.test(previous.tagName)) return previous.textContent.trim();
        if (chartPattern.test(previous.textContent) && previous.textContent.trim().length < 100) return previous.textContent.trim();
        previous = previous.previousElementSibling;
      }
      current = current.parentElement;
    }
    const details = node.closest('details');
    return details?.querySelector('summary')?.textContent.trim() || '';
  }

  function isCandidate(node) {
    if (node.closest('.sleepover-size-chart, cart-items, cart-drawer, .cart-items, form, [role="presentation"]')) return false;
    if (node.tagName === 'TABLE') {
      // RTE measurement/data tables only. Leave embedded forms and layout tables alone.
      return node.rows.length > 0 && !node.querySelector('table, input, select, textarea, button, iframe');
    }
    const description = [node.alt, node.getAttribute('src'), nearbyHeading(node)].join(' ');
    return chartPattern.test(description);
  }

  function markAncestors(node) {
    const ancestors = [];
    const scope = node.closest(contentSelector);
    for (let parent = node.parentElement; parent && parent.id !== 'MainContent' && parent !== document.body; parent = parent.parentElement) {
      if (parent.matches('.shopify-section, .page-width, product-info, product-component')) break;
      if (parent.matches('div, section, figure')) {
        parent.setAttribute('data-sleepover-size-ancestor', '');
        ancestors.push(parent);
      }
      if (parent === scope) break;
    }
    return ancestors;
  }

  function enhance(node) {
    if (instances.has(node) || !isCandidate(node)) return;
    const image = node.tagName === 'IMG';
    const ancestors = markAncestors(node);
    const shell = document.createElement('div');
    shell.className = 'sleepover-size-chart';
    const viewport = document.createElement('div');
    viewport.className = 'sleepover-size-chart__viewport';
    viewport.id = `SleepoverSizeChart-${++sequence}`;
    const hint = document.createElement('p');
    hint.className = 'sleepover-size-chart__hint';
    hint.id = `${viewport.id}-hint`;
    hint.textContent = strings.swipe;
    hint.hidden = true;
    let original = node;
    if (image) {
      if (node.parentElement.tagName === 'A' && node.parentElement.children.length === 1) original = node.parentElement;
      if (original.parentElement.tagName === 'P' && original.parentElement.children.length === 1 && !original.parentElement.textContent.trim()) original = original.parentElement;
    }
    original.before(shell);
    shell.append(viewport, hint);
    viewport.append(original);
    node.setAttribute(image ? 'data-sleepover-size-image' : 'data-sleepover-size-table', '');
    let headingRow = null;
    if (!image) {
      const row = node.rows[0];
      if (row && (row.querySelector('th') || /\b(size|sizes|measurement|measurements|width|length|chest)\b/i.test(row.textContent) || [...row.cells].slice(1).filter(cell => /^(?:[2-6]?X{0,3}[SML]|[2-6]XL)$/i.test(cell.textContent.trim())).length >= 2)) {
        headingRow = row;
        headingRow.setAttribute('data-sleepover-size-heading', '');
      }
    }
    const update = () => {
      if (!mobile.matches || !shell.isConnected) return;
      const visible = viewport.clientWidth > 0;
      const overflows = visible && viewport.scrollWidth > viewport.clientWidth + 2;
      hint.hidden = !overflows;
      if (overflows) {
        viewport.setAttribute('tabindex', '0');
        viewport.setAttribute('role', 'region');
        viewport.setAttribute('aria-label', nearbyHeading(shell) || (image ? strings.image : strings.table));
        viewport.setAttribute('aria-describedby', hint.id);
      } else {
        ['tabindex', 'role', 'aria-label', 'aria-describedby'].forEach(name => viewport.removeAttribute(name));
      }
      if (image) shell.style.setProperty('--sleepover-chart-image-width', `${Math.max(640, node.naturalWidth || 900)}px`);
    };
    let zoom;
    if (image) {
      zoom = document.createElement('button');
      zoom.type = 'button';
      zoom.className = 'sleepover-size-chart__zoom';
      zoom.textContent = strings.enlarge;
      zoom.setAttribute('aria-pressed', 'false');
      zoom.setAttribute('aria-controls', viewport.id);
      zoom.addEventListener('click', () => {
        const active = shell.dataset.zoomed !== 'true';
        shell.dataset.zoomed = String(active);
        zoom.setAttribute('aria-pressed', String(active));
        zoom.textContent = active ? strings.fit : strings.enlarge;
        if (!active) viewport.scrollLeft = 0;
        requestAnimationFrame(update);
      });
      shell.append(zoom);
      node.addEventListener('load', update);
    }
    const observer = 'ResizeObserver' in window ? new ResizeObserver(update) : null;
    observer?.observe(viewport);
    observer?.observe(node);
    const cleanup = () => {
      observer?.disconnect();
      if (image) node.removeEventListener('load', update);
      if (shell.parentNode) shell.replaceWith(original);
      node.removeAttribute(image ? 'data-sleepover-size-image' : 'data-sleepover-size-table');
      headingRow?.removeAttribute('data-sleepover-size-heading');
      ancestors.forEach(parent => {
        if (!parent.querySelector('.sleepover-size-chart')) parent.removeAttribute('data-sleepover-size-ancestor');
      });
    };
    instances.set(node, { update, cleanup, shell });
    requestAnimationFrame(update);
  }

  function refresh() {
    frame = 0;
    instances.forEach((instance, node) => {
      if (!mobile.matches || !instance.shell.isConnected) {
        instance.cleanup();
        instances.delete(node);
      } else instance.update();
    });
    if (mobile.matches) document.querySelectorAll(candidateSelector).forEach(enhance);
  }
  function schedule() {
    if (!frame) frame = requestAnimationFrame(refresh);
  }
  function start() {
    refresh();
    mobile.addEventListener('change', schedule);
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('toggle', schedule, true);
    document.addEventListener('shopify:section:load', schedule);
    // Product/quick-view sections can be re-rendered after selecting a variant.
    const observer = new MutationObserver(records => {
      if (!mobile.matches) return;
      if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && (node.matches('table, img, .sleepover-size-chart') || node.querySelector('table, img'))))) schedule();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
